import { jsPDF } from "jspdf";

/**
 * Professional PDF Generation Service for KPI Appraisals
 * @param {Object} data - The selectedData from the main component
 * @param {String} outputType - "download" to save file, "preview" to return Blob URL
 */
export const generateAppraisalPdf = (data, outputType = "download") => {
  const doc = new jsPDF();
  let currentY = 20;
  const margin = 15;
  const pageWidth = doc.internal.pageSize.getWidth();
  const themeColor = [51, 122, 183]; // Blue brand color

  // --- 1. COORDINATE SETUP ---
  // We use fixed anchor points for the columns to ensure vertical alignment
  const colWeightX = pageWidth - 75;      // Center point for Weight
  const colRatingX = pageWidth - 50;      // Center point for Rating
  const colScoreHeaderX = pageWidth - margin - 2;   // keep header position
  const colScoreValueX  = pageWidth - margin - 5;  // keep header child position

  const checkPageBreak = (neededSpace) => {
    if (currentY + neededSpace > 275) {
      doc.addPage();
      currentY = 20;
      renderWatermark();
    }
  };

  const renderWatermark = () => {
    doc.setFontSize(8);
    doc.setTextColor(200);
    doc.text("Confidential Performance Appraisal", margin, 10);
  };

  // 1. REPORT HEADER
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
  doc.text("KPI PERFORMANCE APPRAISAL REPORT", pageWidth / 2, currentY, { align: "center" });
  currentY += 12;

  // 2. INFORMATION CARDS (EMPLOYEE & RATER)
  doc.setDrawColor(220);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 10;

  doc.setFontSize(9);
  doc.setTextColor(130);
  doc.text("RATEE / EMPLOYEE DETAILS", margin, currentY);
  doc.text("APPRAISER / CONTEXT", pageWidth / 2 + 5, currentY);
  currentY += 6;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(50);
  doc.setFontSize(10);
  doc.text(`Name: ${data.employeeName}`, margin, currentY);
  doc.text(`Appraiser: ${data.appraiserName}`, pageWidth / 2 + 5, currentY);
  currentY += 5;

  doc.setFont("helvetica", "normal");
  doc.text(`Employee ID: ${data.employeeId || "N/A"}`, margin, currentY);
  doc.text(`Period: ${data.period}`, pageWidth / 2 + 5, currentY);
  currentY += 5;

  doc.text(`Position: ${data.position}`, margin, currentY);
  doc.text(`Status: ${data.status?.toUpperCase()}`, pageWidth / 2 + 5, currentY);
  currentY += 15;

  // 3. TABLE HEADER (ALIGNED)
  doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
  doc.rect(margin, currentY, pageWidth - (margin * 2), 10, 'F');
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  
  doc.text("OBJECTIVES & KPI CRITERIA", margin + 5, currentY + 6.5);
  
  // Headers use specific alignment to match data below
  doc.text("WEIGHT", colWeightX, currentY + 6.5, { align: "center" });
  doc.text("RATING", colRatingX, currentY + 6.5, { align: "center" });
  doc.text("SCORE", colScoreHeaderX , currentY + 6.5, { align: "right" });
  currentY += 16;

  let finalScore = 0;

  // 4. CATEGORIES & OBJECTIVES LOOP
  data.performanceCategories?.forEach((cat) => {
    checkPageBreak(15);

    // Render Category Bar
    doc.setFillColor(245, 247, 249);
    doc.rect(margin, currentY - 5, pageWidth - (margin * 2), 8, 'F');
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80);
    doc.text(`CATEGORY: ${cat.description.toUpperCase()}`, margin + 3, currentY);
    currentY += 10;

    cat.objectives?.forEach((obj) => {
      const scoreObj = data.scores?.find(s => s.performanceObjectiveId === obj.id);
      const computed = parseFloat(scoreObj?.computedScore || 0);
      finalScore += computed;

      // Wrap Description and Comments
      const descLines = doc.splitTextToSize(obj.description, 85);
      const commentLines = scoreObj?.comment 
        ? doc.splitTextToSize(`Rater Comment: ${scoreObj.comment}`, pageWidth - (margin * 2) - 10) 
        : [];
      
      const neededHeight = (descLines.length * 5) + (commentLines.length * 5) + 12;
      checkPageBreak(neededHeight);

      // --- ROW DATA (PERFECTLY ALIGNED TO HEADERS) ---
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text(obj.code, margin + 5, currentY);

      // We use the EXACT same X-coordinates and Alignment settings as the Header
      doc.text(`${obj.percentage}%`, colWeightX, currentY, { align: "center" });
      doc.text(`${scoreObj?.score || 0}`, colRatingX, currentY, { align: "center" });
      doc.text(computed.toFixed(2), colScoreValueX, currentY, { align: "right" });
      currentY += 5;

      // Objective Description text
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.setFontSize(8);
      doc.text(descLines, margin + 5, currentY);
      currentY += (descLines.length * 4) + 2;

      // Rater Comment text
      if (scoreObj?.comment) {
        doc.setFont("helvetica", "italic");
        doc.setTextColor(120);
        doc.text(commentLines, margin + 5, currentY);
        currentY += (commentLines.length * 4) + 4;
      }

      // Separator line
      doc.setDrawColor(240);
      doc.line(margin + 5, currentY - 2, pageWidth - margin, currentY - 2);
      currentY += 5;
      doc.setFontSize(9); 
    });
    currentY += 5;
  });

  // 5. SUMMARY FOOTER
  checkPageBreak(30);
  doc.setDrawColor(themeColor[0], themeColor[1], themeColor[2]);
  doc.setLineWidth(0.5);
  doc.line(pageWidth - 100, currentY, pageWidth - margin, currentY);
  currentY += 10;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
  doc.text("FINAL COMPUTED SCORE:", pageWidth - 100, currentY);
  
  // Footer score aligned right with the column above
  doc.text(finalScore.toFixed(2), colScoreValueX, currentY, { align: "right" });

  // 6. PAGE NUMBERS & TIMESTAMP
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(170);
    doc.text(
      `Printed: ${new Date().toLocaleString()} | Page ${i} of ${totalPages}`,
      pageWidth / 2,
      288,
      { align: "center" }
    );
  }

  // --- RETURN OUTPUT ---
  if (outputType === "preview") {
    return doc.output("bloburl");
  } else {
    doc.save(`Appraisal_Report_${data.employeeName.replace(/\s+/g, '_')}.pdf`);
  }
};